import { Application } from '@/types/application';
import { ComparerArg, ComparerParam, ComparerSubroutine, ComparerSubroutineParams } from '@/types/comparer';
import { Metadata } from '@/types/metadata/metadata';
import { Subroutine } from '@/types/subroutine';
import { simpleComparer } from './compareCommon';
import { compareNodes } from './compareNodes';
import { makeParam } from './comparerUtils';

// Pairs each arg with the variable it binds, so a rebinding shows up as a change to that arg.
const withBindings = (subroutine: Subroutine | undefined, field: 'args' | 'event_args'): ComparerArg[] => {
  const bindings = (field === 'args' ? subroutine?.arg_bindings : subroutine?.event_arg_bindings) ?? [];

  return (subroutine?.[field] ?? []).map((arg, index) => ({ ...arg, binding: bindings[index] ?? '' }));
};

const addSubroutine = (
  subA: Subroutine | undefined,
  subB: Subroutine | undefined,
  appA: Application,
  appB: Application,
  id: number,
  metadata: Metadata
): ComparerSubroutine => {
  const changedProps: ComparerParam[] = [
    makeParam('label', subA?.label, subB?.label),
    makeParam('startblock', subA?.startblock, subB?.startblock),
    makeParam('folder', subA?.folder, subB?.folder)
  ];
  const changes: ComparerSubroutineParams = {
    changedProps,
    argChanges: simpleComparer(withBindings(subA, 'args'), withBindings(subB, 'args'), 'id', 'label', [
      'label',
      'type',
      'binding'
    ]),
    eventArgChanges: simpleComparer(withBindings(subA, 'event_args'), withBindings(subB, 'event_args'), 'id', 'label', [
      'label',
      'type',
      'binding'
    ]),
    returnTypeChanges: simpleComparer(subA?.return_types ?? [], subB?.return_types ?? [], 'id', 'label', [
      'label',
      'type'
    ]),
    nodeChanges: compareNodes(subA?.nodes ?? [], subB?.nodes ?? [], appA, appB, metadata)
  };

  if (!subA) return { status: 'added', id, label: subB?.label || '', changes };
  else if (!subB) return { status: 'removed', id, label: subA.label, changes };

  return {
    status:
      changedProps.filter(({ changed }) => changed).length +
      changes.argChanges.filter(({ status }) => status !== 'unchanged').length +
      changes.eventArgChanges.filter(({ status }) => status !== 'unchanged').length +
      changes.returnTypeChanges.filter(({ status }) => status !== 'unchanged').length +
      changes.nodeChanges.filter(({ status }) => status !== 'unchanged').length
        ? 'modified'
        : 'unchanged',
    id,
    label: subA.label,
    changes
  };
};

export const compareSubroutines = (appA: Application, appB: Application, metadata: Metadata): ComparerSubroutine[] => {
  const result: ComparerSubroutine[] = [];

  const mapA = new Map(appA.subroutines.map((subroutine) => [subroutine.id, subroutine]));
  const mapB = new Map(appB.subroutines.map((subroutine) => [subroutine.id, subroutine]));
  const allSubroutineIds = new Set([...mapA.keys(), ...mapB.keys()]);

  allSubroutineIds.forEach((subroutineId) => {
    const subA = mapA.get(subroutineId);
    const subB = mapB.get(subroutineId);

    result.push(addSubroutine(subA, subB, appA, appB, subroutineId, metadata));
  });

  return result;
};
